import { Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { tap, switchMap } from 'rxjs/operators'
import { Observable, from, BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';
import { CacheService } from './cache.service';
import { AppService } from './app.service';
import { AlertController } from '@ionic/angular';
import { Router } from '@angular/router';
import { iUser } from '../interfaces/user';
import { UserService } from './user.service';
import { ToastService } from './toast.service';

@Injectable({
	providedIn: 'root'
})
export class AuthenticationService {


	// ---- Variables ---- \\
	private url = '/oauth/token'
	private authToken = environment.auth_token
	private authState = new BehaviorSubject(false)
	
	public user: WritableSignal<iUser | null> = signal(null);


	// ---- Constructor ---- \\
	constructor(
		private http: HttpClient,
		private storage: StorageService,
		private cache: CacheService,
		private app: AppService,
		private userSerivce: UserService,
		private alertController: AlertController,
		private router: Router,
		private toast: ToastService
	) {
	}


	// ---- Public ---- \\


	/**
	 * Login the user by authenticating and setting tokens in storage
	 * 
	 * @param username 
	 * @param password 
	 * @returns 
	 */
	public login(username: string, password: string) {
		if (username && password) {
			return this.requestTokenByCredentials(username, password);
		}
		// TODO Return error if username or password is missing
	}


	/**
	 * Check if user is logged in by the authState
	 * 
	 * @returns BehaviorSubject value
	 */
	public isLoggedIn() {
		return this.authState.value;
	}


	/**
	 *  Gets refresh token and requests new token
	 */
	public refreshToken() {
		return from(this.getRefreshToken()).pipe(
			switchMap(refresh_token => {
				return this.requestTokenByRefreshToken(refresh_token)
			})
		)
	}


	/**
	 * Prompt the user to logout
	 */
	public async logout() {
		const alert = await this.alertController.create({
			header: 'Vil du logge ud?',
			buttons: [
				{
					text: 'Fortryd'
				},
				{
					text: 'Log af',
					handler: () => {
						this.killSession()
						this.router.navigate(['/'])
					}
				}
			]
		});

		await alert.present();
	}


	/**
	 * Validate the token against token timestamp
	 * Primary used in AuthGuard
	 * 
	 * @returns true/false
	 */
	public async validateToken(): Promise<boolean> {
		try {
			const token = await this.getToken();
			if (!token) {
				this.killSession();
				return false;
			}

			const tokenTime = await this.getTokenTimestamp();
			const unixTimeNow = Date.now();

			if (tokenTime >= unixTimeNow) {
				this.authState.next(true);
				return true;
			} else {
				this.killSession();
				return false;
			}
		} catch (error) {
			this.killSession();
			return false;
		}
	}



	/**
	 * Get stored user data
	 */
	public getStoredUser() {
		this.storage.get('user').then((userData: iUser) => {
			if (userData) {
				this.user.set(userData)
			}

		})
	}


	/**
	 * Update user data signal and local storage
	*/	
	public updateUserData(userData: iUser) {
		this.saveUserData(userData)
		this.user.set(userData)
	}


	/**
	 * Returns WP-user role
	 */
	public getUserRoles() {
		return this.user()?.user_roles
	}


	/**
	 * Returns access token
	 */
	public getToken(): Promise<any> {
		return this.storage.get('token')
	}


	/**
	 * Returns token timeout-stamp
	 */
	public getTokenTimestamp(): Promise<any> {
		return this.storage.get('token-timestamp')
	}


	/**
	 * Logout the user without asking by removing all auth tokens, timestamp and auth state
	 */
	public killSession() {
		// Remove login related items from storage
		this.storage.remove('token')
		this.storage.remove('token-timestamp')
		this.storage.remove('refresh-token')
		this.storage.remove('user')
		this.storage.remove('api-override')
		this.storage.remove('packs')

		// Set login state to false
		this.authState.next(false)

		// Remove cached items
		this.cache.clearCache()
	}



	// ---- Private ---- \\

	/**
	 * Request a token by user cridentials for futher API communication
	 * 
	 * @param username 
	 * @param password 
	 * @returns 
	 */
	private requestTokenByCredentials(username: string, password: string) {
		const body = {
			grant_type: 'password',
			username,
			password
		}

		return this.requestToken(body)
	}


	/**
	 * Request a refresh token for refreshing main token
	 * 
	 * @param refresh_token 
	 * @returns 
	 */
	private requestTokenByRefreshToken(refresh_token: string) {
		const body = {
			grant_type: 'refresh_token',
			refresh_token
		}

		return this.requestToken(body)
	}


	/**
	 * Request a token by body headers
	 * 
	 * @param body
	 * @returns 
	 */
	private requestToken(body): Observable<any> {
		const url = this.app.getApiEndpoint() + this.url;
		const options = {
			headers: new HttpHeaders({
				'Content-Type': 'application/json',
				Authorization: 'Basic ' + this.authToken
			})
		};

		return this.http.post(url, body, options)
			.pipe(
				switchMap((response: any) => {
					if (response.access_token) {
						// Save tokens to storage and wait for completion
						// saveToken returnerer et Promise<void>, så vi skal returnere en Observable med en værdi
						return from(Promise.resolve(this.saveToken(response.access_token))).pipe(
							switchMap(() => {
								this.saveTokenTimeout(response.expires_in);
								this.saveRefreshToken(response.refresh_token);

								// Approve user access
								this.authState.next(true);

								// Fetch user data after token is saved
								return this.userSerivce.getUserData().pipe(
									tap((userData: iUser) => {
										this.saveUserData(userData);
									})
								);
							})
						);
					} else if (response.error) {
						this.toast.presentToast(response.error, 'danger')
					} else {
						this.toast.presentToast('Ukendt fejl ved login', 'danger')
					}
				})
			);
	}


	/**
	 * Returns refresh token
	 */
	private getRefreshToken(): Promise<any> {
		return this.storage.get('refresh-token')
	}


	/**
	 * Saves access token in storage with `token` identifier
	 * 
	 * @param token 
	 */
	private saveToken(token: string): Promise<boolean> {
		return this.storage.set('token', token)
			.then(() => true)
			.catch(() => false);
	}


	/**
	 * Current time + token expiration time for auth-check
	 * 
	 * @param timestamp 
	 */
	private saveTokenTimeout(expiration: number) {
		this.storage.set('token-timestamp', (expiration * 1000) + Date.now())
	}


	/**
	 * Saves refresh token in storage with `refresh-token` identifier
	 * 
	 * @param token 
	 */
	private saveRefreshToken(token: string) {
		this.storage.set('refresh-token', token)
	}


	/**
	 * Save user data in storage
	 * 
	 * @param userData
	 */
	private saveUserData(userData: any) {
		this.storage.set('user', userData)
		this.user.set(userData)
	}

}